import React from 'react';
import { Check, Printer, Share2 } from 'lucide-react';

interface PrintShareToggleProps {
  checked: boolean;
  onChange: (value: boolean) => void;
  /** true → render the Share icon, false → render the Printer icon */
  share?: boolean;
  label: string;
  theme: any;
  className?: string;
}

/**
 * Attractive, theme-aware "Print / Share" checkbox shown next to the
 * Place Order / Settle actions. Renders as a pill that fills with the primary
 * colour and grows a soft focus ring when selected.
 */
export const PrintShareToggle: React.FC<PrintShareToggleProps> = ({
  checked,
  onChange,
  share,
  label,
  theme,
  className = '',
}) => {
  const p = theme.colors.primary;
  const Icon = share ? Share2 : Printer;

  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
      className={`group inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border transition-all duration-200 select-none active:scale-[0.97] ${className}`}
      style={{
        borderColor: checked ? p[500] : theme.colors.border.primary,
        backgroundColor: checked ? p[50] : 'transparent',
        boxShadow: checked ? `0 0 0 3px ${p[100]}` : 'none',
      }}
    >
      <span
        className="flex items-center justify-center h-5 w-5 rounded-md border-2 transition-all duration-200"
        style={{
          borderColor: checked ? p[600] : theme.colors.border.secondary,
          backgroundColor: checked ? p[600] : 'transparent',
        }}
      >
        <Check
          className="h-3.5 w-3.5 text-white transition-transform duration-200"
          strokeWidth={3}
          style={{ transform: checked ? 'scale(1)' : 'scale(0)' }}
        />
      </span>
      <Icon
        className="h-4 w-4 transition-colors"
        style={{ color: checked ? p[600] : theme.colors.text.secondary }}
      />
      <span
        className="text-sm font-semibold transition-colors"
        style={{ color: checked ? p[700] : theme.colors.text.primary }}
      >
        {label}
      </span>
    </button>
  );
};
